import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { LOCAL_STORAGE_KEY } from "../constants";
import Routers from "../constants/routers";
import { useUser } from "../context/UserContext";
import { getAdminApplicationEvents } from "../services/api/adminApplicationEventsApi";
import { logoutApi } from "../services/api/logoutApi";
import { AdminApplicationEvent } from "../types/adminApplicationEvent";
import { handleMutationError } from "../utils/common/errors";
import { TextWithTooltip } from "../utils/common/textWithTooltip";
import GlobalSearch from "./GlobalSearch";
import "./Header.css";
import { menuItems } from "./Sidebar";
import { getCurrentPageTitle } from "./currentPageTitle";

const ADMIN_APPLICATION_EVENT_POLL_MS = 30000;
const EVENT_START_KEY_PREFIX = "capco_admin_application_event_started_at";
const EVENT_TOASTED_KEYS_PREFIX = "capco_admin_application_event_toasted_keys";

const readJsonArray = (key: string): string[] => {
  try {
    const rawValue = sessionStorage.getItem(key);
    if (!rawValue) return [];

    const parsed = JSON.parse(rawValue);
    return Array.isArray(parsed) ? parsed.filter((value) => typeof value === "string") : [];
  } catch {
    return [];
  }
};

const writeJsonArray = (key: string, values: string[]) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(values));
  } catch {
    // Ignore storage failures; the next poll can still fetch events safely.
  }
};

const getOrCreateStartedAt = (key: string): string => {
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;

    const startedAt = new Date().toISOString();
    sessionStorage.setItem(key, startedAt);
    return startedAt;
  } catch {
    return new Date().toISOString();
  }
};

const Header: React.FC = () => {
  const { userAuth } = useUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pageTitle = getCurrentPageTitle(pathname, menuItems);
  const storageScope = String(userAuth?.user?.u_code ?? userAuth?.user?.login_id ?? "current");
  const startedAtKey = `${EVENT_START_KEY_PREFIX}:${storageScope}`;
  const toastedKeysKey = `${EVENT_TOASTED_KEYS_PREFIX}:${storageScope}`;
  const [notificationStartedAt, setNotificationStartedAt] = useState<string | null>(null);

  const displayUserName = useMemo(() => {
    const name = `${userAuth?.user?.u_lname || ""}${userAuth?.user?.u_fname || ""}`.trim();
    return name;
  }, [userAuth?.user?.u_fname, userAuth?.user?.u_lname]);

  const { data: adminApplicationEvents } = useQuery({
    queryKey: ["admin_application_events", notificationStartedAt],
    queryFn: () => getAdminApplicationEvents(notificationStartedAt as string),
    enabled: Boolean(notificationStartedAt),
    refetchInterval: ADMIN_APPLICATION_EVENT_POLL_MS,
    retry: false
  });

  const logoutMutation = useMutation({
    mutationFn: () => logoutApi(),
    onSuccess: () => {
      localStorage.removeItem(LOCAL_STORAGE_KEY.ACCESS_TOKEN);
      window.location.href = window.location.origin + Routers.Login;
    },
    onError: (error: any) => {
      handleMutationError(error);
    }
  });

  const handleLogout = async () => {
    logoutMutation.mutate();
  };

  const showApplicationEventToast = useCallback(
    (event: AdminApplicationEvent) => {
      toast(
        <div className="admin-application-event-toast">
          <div className="admin-application-event-toast__title">{event.title}</div>
          {event.summary && <div className="admin-application-event-toast__summary">{event.summary}</div>}
          <button className="admin-application-event-toast__link" onClick={() => navigate(event.detail_path)}>
            詳細を開く
          </button>
        </div>,
        {
          type: "success",
          position: "top-right",
          autoClose: 8000,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          closeButton: true,
          style: {
            borderLeft: "4px solid #16a34a"
          }
        }
      );
    },
    [navigate]
  );

  const showAggregateApplicationEventToast = useCallback(
    (events: AdminApplicationEvent[]) => {
      const newestEvent = events[0];
      toast(
        <div className="admin-application-event-toast">
          <div className="admin-application-event-toast__title">新着申請が{events.length}件あります</div>
          {newestEvent?.summary && (
            <div className="admin-application-event-toast__summary">最新: {newestEvent.summary}</div>
          )}
          {newestEvent?.detail_path && (
            <button className="admin-application-event-toast__link" onClick={() => navigate(newestEvent.detail_path)}>
              最新の詳細を開く
            </button>
          )}
        </div>,
        {
          type: "success",
          position: "top-right",
          autoClose: 9000,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          closeButton: true,
          style: {
            borderLeft: "4px solid #16a34a"
          }
        }
      );
    },
    [navigate]
  );

  useEffect(() => {
    if (!userAuth?.user) return;

    setNotificationStartedAt(getOrCreateStartedAt(startedAtKey));
  }, [startedAtKey, userAuth?.user]);

  useEffect(() => {
    if (!notificationStartedAt || !adminApplicationEvents?.events?.length) return;

    const startedAtTime = new Date(notificationStartedAt).getTime();
    const toastedKeys = new Set(readJsonArray(toastedKeysKey));
    const freshEvents = adminApplicationEvents.events.filter((event) => {
      if (!event.event_key || !event.occurred_at || toastedKeys.has(event.event_key)) {
        return false;
      }

      return new Date(event.occurred_at).getTime() > startedAtTime;
    });

    if (!freshEvents.length) return;

    freshEvents.forEach((event) => toastedKeys.add(event.event_key));
    writeJsonArray(toastedKeysKey, Array.from(toastedKeys).slice(-200));

    if (freshEvents.length > 3) {
      freshEvents.slice(0, 3).forEach(showApplicationEventToast);
      showAggregateApplicationEventToast(freshEvents.slice(3));
      return;
    }

    freshEvents.forEach(showApplicationEventToast);
  }, [
    adminApplicationEvents?.events,
    notificationStartedAt,
    showAggregateApplicationEventToast,
    showApplicationEventToast,
    toastedKeysKey
  ]);

  return (
    <header className="header">
      <div className="header-left">
        <img src="/capco-agency-logo.jpg" alt="Capco Agency Logo" className="header-logo" />
        <h1 className="header-title">カプコ基幹システム</h1>
        {pageTitle && (
          <span className="header-page-title" title={pageTitle} aria-label="現在のページ">
            {pageTitle}
          </span>
        )}
      </div>

      <div className="header-center">
        <GlobalSearch />
      </div>

      <div className="header-right">
        <div className="user-menu">
          <div className="user-info">
            {/* 
              Note: userAuth data comes from authenticated API (getMe), not user-generated content.
              React automatically escapes content in JSX, providing XSS protection.
              If this ever displays user-generated content, consider additional sanitization.
            */}
            <span className="user-name">{TextWithTooltip(displayUserName)}</span>
            {/* <span className="user-role">{userAuth?.u_fname || ""}</span> */}
          </div>
          <button className="logout-button" onClick={handleLogout}>
            ログアウト
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
